import * as React from "react";
import { PageLayout } from "../../ui-components/PageLayout";
import { RootState } from "../../store/reducers";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { AppDispatch } from "../../store/store";
import { getUser } from "../../store/modules/user/userActions";
import { createCustomerPortalSession } from "../../store/modules/subscription/subscriptionActions";
import { useLocation, useNavigate } from "react-router-dom";

import LoadingDialog from "../../ui-components/Dialogs/LoadingDialog";
import { useMsal } from "@azure/msal-react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import theme from "../../styles/theme";
import Button from "@mui/material/Button";
import { loginRequest } from "../../authConfig";
declare module "react" {
  namespace JSX {
    interface IntrinsicElements {
      "stripe-pricing-table": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}
function Pricing() {
  const { currentUser: user, loading: userLoading } = useSelector(
    (state: RootState) => state?.user
  );
  const navigate = useNavigate();

  const { isAuthenticated } = useSelector((state: RootState) => state?.auth);
  const { instance, inProgress } = useMsal();
  const [isLoading, setIsLoading] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [userLoadingRetried, setUserLoadingRetried] = React.useState(false);
  const [pollAttempts, setPollAttempts] = React.useState(0);

  const account = instance.getActiveAccount();

  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  if (user?.subscriptionId && !location.pathname.endsWith("/confirmed")) {
    dispatch(createCustomerPortalSession());
  }

  useEffect(() => {
    // If MSAL says it's done but we still don't have account after reasonable time
    if (inProgress === "none" && !account) {
      const timer = setTimeout(() => {
        setShowError(true);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [inProgress, account]);

  useEffect(() => {
    const isConfirmed = location.pathname.endsWith("/confirmed");
    const MAX_POLL_ATTEMPTS = 10;
    const POLL_INTERVAL = 3000; // 3 seconds

    if (!isConfirmed || !account || !isAuthenticated || user?.isAdmin) {
      return;
    }

    // Check if user is already subscribed
    const isSubscribed =
      user?.subscriptionStatus === "active" ||
      user?.subscriptionStatus === "trialing";

    if (isSubscribed) {
      setIsLoading(false);
      return;
    }

    // Start polling only if not subscribed and haven't exceeded max attempts
    if (pollAttempts < MAX_POLL_ATTEMPTS) {
      setIsLoading(true);

      const pollInterval = setInterval(() => {
        setPollAttempts((prev) => prev + 1);
        dispatch(getUser());
      }, POLL_INTERVAL);

      return () => clearInterval(pollInterval);
    } else {
      // Max attempts reached
      setIsLoading(false);
    }
  }, [
    account,
    isAuthenticated,
    user?.isAdmin,
    user?.subscriptionStatus,
    pollAttempts,
    location.pathname,
    dispatch,
  ]);

  useEffect(() => {
    if (userLoading && !user && !userLoadingRetried) {
      const timer = setTimeout(() => {
        dispatch(getUser());
        setUserLoadingRetried(true);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [userLoading, user, userLoadingRetried, dispatch]);

  useEffect(() => {
    const isConfirmed = location.pathname.endsWith("/confirmed");

    if (
      isConfirmed &&
      (user?.subscriptionStatus === "active" ||
        user?.subscriptionStatus === "trialing")
    ) {
      navigate("/dashboard");
    }
  }, [user?.subscriptionStatus, location.pathname, navigate]);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleRetry = async () => {
    setShowError(false);
    try {
      await instance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account: instance.getAllAccounts()[0],
      });
    } catch (error) {
      instance.loginRedirect(loginRequest);
    }
  };

  const getLoadingMessage = () => {
    if (isLoading) {
      if (pollAttempts >= 10) {
        return "Subscription activation is taking longer than expected. Please refresh the page or contact support.";
      }
      return `Verifying your subscription...`;
    }
    if (inProgress !== "none") return "Signing you in...";
    return undefined;
  };

  return (
    <PageLayout>
      <Box sx={{ mb: 6, ml: isMobile ? 0 : 2 }}>
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            color="primary"
            component="h1"
            fontWeight="bold"
          >
            Pricing
          </Typography>
        </Box>
        <script async src="https://js.stripe.com/v3/pricing-table.js"></script>
        <LoadingDialog
          open={isLoading || inProgress !== "none"}
          message={getLoadingMessage()}
        />

        {showError && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error" sx={{ mb: 1 }}>
              Unable to load account information.
            </Typography>
            <Button variant="contained" onClick={handleRetry}>
              Retry Sign In
            </Button>
          </Box>
        )}

        {account ? (
          <stripe-pricing-table
            client-reference-id={account.idTokenClaims?.oid}
            customer-email={account.username}
            pricing-table-id={window.__config.STRIPE_PRICING_TABLE_ID}
            publishable-key={window.__config.STRIPE_PUBLISHABLE_KEY}
          ></stripe-pricing-table>
        ) : !showError ? (
          <Typography>Loading pricing...</Typography>
        ) : null}
      </Box>
    </PageLayout>
  );
}

export default Pricing;
